import type { InventoryVehicle } from '@carvana/merch-contracts';
import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile/utils/availability';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext } from 'react';
import TagId from '~/constants/common/TagId';
import AdobeEventPageType from '~/constants/dealership/AdobeEventPageType';
import EventLocation from '~/constants/dealership/EventLocation';
import { COOKIES } from '~/constants/srp';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useFinanceInfo } from '~/context/srp/FinanceContext';
import { useInventoryBulkData } from '~/context/srp/InventoryContext';
import {
  useKeywordSuggestion,
  useSearchSource
} from '~/context/srp/SearchEventsDataContext';
import StaticContext from '~/context/srp/StaticContext';
import type { MegaEventVehicle } from '~/contracts/common';
import usePushToAdobe from '~/hooks/dealership/usePushToAdobe';
import useGetSortByChanged from '~/hooks/srp/useGetSortByChanged';
import BrowserCookies from '~/utilities/BrowserCookies';
import getPurchaseStatus from '~/utilities/getPurchaseStatus';
import useDataForAnalytics from './useDataForAnalytics';

const defaultAvailability = {
  soonestDelivery: { content: undefined },
  soonestPickup: { content: undefined }
};

function getPriceDropValue(vehicle: Partial<InventoryVehicle>) {
  return vehicle.previousPrice && vehicle.price?.total
    ? vehicle.previousPrice - vehicle.price.total
    : 0;
}

export default function useSendTileInfoEvent() {
  const { partnerId } = useContext(StaticContext);
  const { zip } = useUserLocation();
  const {
    appliedFilters,
    currentPage,
    isDefaultFilters,
    searchRequestId,
    similarVehicles,
    sortBy,
    totalMatchedInventory,
    tradeInfo,
    vehicles
  } = useDataForAnalytics();
  const inventoryBulkData = useInventoryBulkData();
  const financeInfo = useFinanceInfo();
  const isInitialSortByRef = useGetSortByChanged();
  const keywordSuggestion = useKeywordSuggestion();
  const { platform, isNative } = useNativeDeviceType();
  const pushToAdobe = usePushToAdobe();
  const searchSource = useSearchSource();
  const trackEvent = useTrackEvent();

  return useCallback(
    (vehicle: Partial<InventoryVehicle> = {}) => {
      if (!vehicle.vehicleId) return;

      const { soonestDelivery, soonestPickup } =
        inventoryBulkData[vehicle.vehicleId] ?? defaultAvailability;
      const { getItBy, routeId, utcSoonestDateTimeIso } = calculateGetItBy(
        calculateSoonestAvailable({
          delivery: soonestDelivery.content,
          pickup: soonestPickup.content
        })
      );

      const {
        isSimilarTile,
        locationId,
        offering,
        price,
        previousPrice,
        vehicleId,
        vehicleInventoryType,
        vehicleTags,
        transportCost
      } = vehicle;
      const {
        apr,
        discounts,
        downPaymentDetails,
        estimatedTaxesAndFees,
        feesMinusShipping,
        monthlyPayment,
        pricingErrorMessage,
        term,
        total,
        totalTaxes
      } = price ?? {};

      const totalCashDue = downPaymentDetails?.totalCashDue;
      const hasTerms = totalCashDue != null && monthlyPayment != null;

      const tileIndex = [...vehicles, ...similarVehicles].findIndex(
        (v: InventoryVehicle) => v.vehicleId === vehicleId
      );

      const { isLocked, isOnHold } = getPurchaseStatus(vehicle);

      const eventData: MegaEventVehicle = {
        // @ts-expect-error we don't know if this property is needed in the event
        appliedFilters,
        currentPage,
        financeInfo,
        isGreatDeal:
          vehicle.vehicleTags?.some(tag => tag.tagId === TagId.GreatDeal) ===
          true,
        isOpenSearch: isDefaultFilters && isInitialSortByRef,
        isPriceDrop: getPriceDropValue(vehicle ?? {}),
        isSimilarTile,
        isWebview: isNative,
        isZeroDown: vehicle?.price?.downPayment === 0,
        keywordSuggestion,
        locationId,
        offering,
        partnerId,
        searchRequestId,
        searchSource,
        sortBy,
        tileIndex,
        tileInfo: {
          apr,
          discounts,
          additionalCashDown: downPaymentDetails?.additionalCashDown,
          estimatedTaxesAndFees: estimatedTaxesAndFees ?? undefined,
          feesMinusShipping,
          getItBy,
          hasTerms,
          isLocked,
          isOnHold,
          monthlyPayment,
          nonRefundableShippingFees:
            downPaymentDetails?.nonRefundableShippingFees,
          pricingErrorMessage,
          previousPrice,
          routeId,
          term,
          total,
          totalCashDue: hasTerms ? Number(totalCashDue) : totalCashDue,
          totalTaxes,
          transportCost,
          utcSoonestDateTimeIso
        },
        totalMatchedInventory,
        tradesInfo: {
          applyTradeIn: tradeInfo?.applyTradeIn,
          status:
            tradeInfo?.status && tradeInfo?.totalTradeInValue === 0
              ? 'offer pending'
              : tradeInfo?.status,
          totalTradeInEquity: tradeInfo?.totalTradeInEquity,
          totalTradeInValue: tradeInfo?.totalTradeInValue
        },
        vehicleId,
        vehicleInventoryType,
        vehicleTags,
        webview: isNative
          ? { platform, version: BrowserCookies.get(COOKIES.nativeAppVersion) }
          : null,
        zip
      };

      trackEvent('clicked-tile-finance-terms-info', eventData);
      trackEvent('Vehicle Tile', {
        action: 'clicked',
        item: 'tile',
        ...eventData
      });

      datadogRum.addAction('clicked-tile-finance-terms-info', eventData);
      datadogRum.addAction('Vehicle Tile', {
        action: 'clicked',
        item: 'tile',
        ...eventData
      });

      pushToAdobe({
        event: 'interaction-click',
        interaction: {
          site: 'dealer',
          type: 'nav',
          page: AdobeEventPageType.VehicleListing,
          location: EventLocation.Body,
          description: [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
            .join('-')
            .replaceAll(' ', '-')
            .toLowerCase()
        }
      });
    },
    [
      appliedFilters,
      currentPage,
      financeInfo,
      inventoryBulkData,
      isDefaultFilters,
      isInitialSortByRef,
      isNative,
      keywordSuggestion,
      partnerId,
      platform,
      pushToAdobe,
      searchRequestId,
      searchSource,
      similarVehicles,
      sortBy,
      totalMatchedInventory,
      trackEvent,
      tradeInfo?.applyTradeIn,
      tradeInfo?.status,
      tradeInfo?.totalTradeInEquity,
      tradeInfo?.totalTradeInValue,
      vehicles,
      zip
    ]
  );
}
